<template>
  <EditorTabFrame
    :loading="loading"
    :loaded="loaded"
    :load-error="loadError"
    :saving="saving"
    :dirty="isDirty"
    :conflict-message="conflictMessage"
    @save="save"
    @discard="discard"
    @reload="load"
  >
    <div v-if="form && lookups">
      <EditorSection
        title="Meios de pagamento"
        description="Meios aceitos para o público externo (não funcionários) e o gateway que processa cada um."
      >
        <span class="ed-label">Meios aceitos</span>
        <div class="ed-check-grid mb-3">
          <v-checkbox
            v-for="method in publicMethods"
            :key="method.id"
            :input-value="form.paymentMethodIds.indexOf(method.id) >= 0"
            :label="method.name"
            color="primary"
            hide-details
            @change="toggleMethod(method.id, $event)"
          ></v-checkbox>
        </div>

        <div class="ed-subtitle">Gateways</div>
        <div class="ed-grid ed-grid--3">
          <div v-for="providerSlot in providerSlots" :key="providerSlot.key">
            <v-select
              v-model="form.providers[providerSlot.key]"
              :items="providersFor(providerSlot.methodId)"
              item-value="id"
              item-text="name"
              :label="providerSlot.label"
              :clearable="!providerSlot.required"
            ></v-select>
          </div>
        </div>
      </EditorSection>

      <EditorSection title="Parcelamento e recorrência" description="Até quantas vezes cada meio pode ser parcelado e quais periodicidades de doação recorrente são oferecidas.">
        <div class="ed-grid ed-grid--3">
          <div v-for="parcelament in visibleParcelaments" :key="parcelament.paymentMethodId" class="ed-panel ed-parcelament">
            <div class="ed-subtitle">{{ methodName(parcelament.paymentMethodId) }}</div>
            <v-select v-model="parcelament.parcelament" :items="howManyOptions" label="Parcelas"></v-select>
            <InputMoney
              v-model="parcelament.minimumParcelamentValue"
              textLabel="Valor mínimo da parcela"
              :precision="0"
              :required="false"
              :autoFocusOnValidationFail="false"
            />
          </div>
        </div>
        <div v-if="!visibleParcelaments.length" class="ed-empty">Marque cartão, boleto ou Pix em "Meios aceitos" para configurar o parcelamento.</div>
        <div v-if="showIncomeTaxAlert" class="ed-note ed-note--warning mt-3">
          <i class="fal fa-exclamation-triangle"></i>
          <span>
            Campanha incentivada com parcelamento: só os valores pagos até o fim do ano (28/12 no Pix, 26/12 no boleto e no cartão)
            podem ser deduzidos no IRPF do ano seguinte.
          </span>
        </div>

        <EditorRecurrenceList class="mt-4" v-model="form.recurrencePeriods" :periods="lookups.donationRecurrencePeriods" />
      </EditorSection>

      <EditorSection title="Taxas" description="Quem paga a taxa de cada meio e as taxas cobradas da campanha.">
        <div class="ed-grid ed-grid--4">
          <div v-for="owner in taxOwners" :key="owner.key" class="ed-radio-row">
            <span class="ed-label">{{ owner.label }}</span>
            <v-radio-group v-model="form[owner.key]" hide-details>
              <v-radio label="A empresa" :value="1" color="primary"></v-radio>
              <v-radio label="O doador" :value="2" color="primary"></v-radio>
            </v-radio-group>
          </div>
        </div>

        <div class="ed-grid ed-grid--4 mt-4">
          <div>
            <v-select v-model="form.receiver" :items="receivers" label="Recebedor" clearable></v-select>
          </div>
          <div>
            <InputMoney v-model="form.contractualFee" textLabel="Taxa contratual" :precision="2" prefix="% " :required="false" :autoFocusOnValidationFail="false" />
          </div>
          <div>
            <InputMoney v-model="form.taxAUC" textLabel="Taxa AUC" :precision="2" prefix="% " :required="false" :autoFocusOnValidationFail="false" />
          </div>
          <div>
            <InputMoney v-model="form.ticketFixedValue" textLabel="Valor fixo do boleto" :precision="2" prefix="R$ " :required="false" :autoFocusOnValidationFail="false" />
          </div>
          <div v-if="canEditTicketConfig">
            <v-select
              v-model="form.ticketConfigId"
              :items="lookups.ticketConfigs"
              item-value="id"
              item-text="name"
              label="Convênio de boleto"
              hint="Vazio usa o convênio padrão"
              persistent-hint
              clearable
            ></v-select>
          </div>
        </div>

        <v-btn flat color="primary" class="px-0 mt-2 ed-text-btn" @click="taxDialog = true">
          <i class="fal fa-sliders-h mr-2"></i>Taxas por gateway (salvas à parte)
        </v-btn>
        <TaxesConfig v-if="taxDialog" :campaignId="campaignId" v-model="taxDialog" />
      </EditorSection>

      <EditorSection title="Público interno" description="Empresas do grupo que participam como funcionários, com seus meios de pagamento e recorrências.">
        <EditorCompanyPayments
          v-model="form.companies"
          :payment-methods="lookups.paymentMethods"
          :recurrence-periods="lookups.donationRecurrencePeriods"
        />
      </EditorSection>
    </div>
  </EditorTabFrame>
</template>

<script>
import editorTab from "../editorTab.mixin";
import EditorTabFrame from "../EditorTabFrame.vue";
import EditorSection from "../EditorSection.vue";
import EditorRecurrenceList from "../EditorRecurrenceList.vue";
import EditorCompanyPayments from "../EditorCompanyPayments.vue";
import InputMoney from "@/components/input/InputMoney.vue";
import TaxesConfig from "@/components/TaxesConfig.vue";
import CampaignEditorService from "@/scripts/services/campaignEditor.service";
import PaymentMethod from "@/scripts/models/enums/paymentMethod.enum";
import { CampaignTypes } from "@/scripts/models/enums/campaignTypes.enum";

const service = new CampaignEditorService();
const PARCELAMENT_METHODS = Object.freeze([PaymentMethod.CreditCard, PaymentMethod.Ticket, PaymentMethod.Pix]);

export default {
  name: "PaymentsTab",
  mixins: [editorTab],
  components: { EditorSection, EditorTabFrame, EditorRecurrenceList, EditorCompanyPayments, InputMoney, TaxesConfig },
  data() {
    return {
      lookups: null,
      canEditTicketConfig: false,
      accountDebitAvailable: false,
      campaignTypeId: null,
      donationForTheNextYear: false,
      taxDialog: false,
      receivers: Object.freeze([
        { value: 1, text: "2 dias" },
        { value: 2, text: "30 dias" },
        { value: 3, text: "Recebedor Porto" },
      ]),
      taxOwners: Object.freeze([
        { key: "taxOwnerPix", label: "Taxa do Pix" },
        { key: "taxOwnerTicket", label: "Taxa do boleto" },
        { key: "taxOwnerCreditCard", label: "Taxa do cartão" },
        { key: "taxOwnerPixAutomatic", label: "Taxa do Pix automático" },
      ]),
    };
  },
  computed: {
    providerSlots() {
      const slots = [
        { key: "creditCard", methodId: PaymentMethod.CreditCard, label: "Cartão de crédito *", required: true },
        { key: "ticket", methodId: PaymentMethod.Ticket, label: "Boleto *", required: true },
        { key: "pix", methodId: PaymentMethod.Pix, label: "Pix" },
        { key: "pixAutomatic", methodId: PaymentMethod.PixAutomatic, label: "Pix automático" },
      ];
      if (this.accountDebitAvailable) slots.push({ key: "accountDebit", methodId: PaymentMethod.AccountDebit, label: "Débito em conta" });
      return slots;
    },
    publicMethods() {
      return this.lookups.paymentMethods.filter((method) => method.id !== PaymentMethod.AccountDebit || this.accountDebitAvailable);
    },
    visibleParcelaments() {
      return this.form.parcelaments.filter((item) => this.form.paymentMethodIds.indexOf(item.paymentMethodId) >= 0);
    },
    howManyOptions() {
      // Mesma regra do getHowMany da tela antiga: sem "doação para o ano seguinte", só até dezembro.
      const max = this.donationForTheNextYear ? 12 : Math.max(1, 12 - (new Date().getMonth() + 1));
      return Array.from({ length: max + 1 }, (unused, n) => ({ value: n, text: n === 0 ? "Sem parcelamento" : `${n}x` }));
    },
    showIncomeTaxAlert() {
      return this.campaignTypeId === CampaignTypes.Incentivada && this.visibleParcelaments.some((item) => item.parcelament > 1);
    },
  },
  methods: {
    fetchSection() {
      return Promise.all([service.getLookups(), service.getPayments(this.campaignId)]).then(([lookups, dto]) => {
        this.lookups = lookups;
        return dto;
      });
    },
    saveSection(body) {
      return service.savePayments(body);
    },
    toForm(dto) {
      const parcelaments = PARCELAMENT_METHODS.map((methodId) => {
        const stored = (dto.parcelaments || []).find((item) => item.paymentMethodId === methodId);
        return { paymentMethodId: methodId, parcelament: stored ? stored.parcelament : 0, minimumParcelamentValue: stored ? stored.minimumParcelamentValue : 0 };
      });
      const providers = dto.providers || {};
      return {
        paymentMethodIds: (dto.paymentMethodIds || []).slice(),
        providers: {
          creditCard: providers.creditCard || null,
          ticket: providers.ticket || null,
          accountDebit: providers.accountDebit || null,
          pix: providers.pix || null,
          pixAutomatic: providers.pixAutomatic || null,
        },
        parcelaments,
        recurrencePeriods: (dto.recurrencePeriods || []).map((item) => Object.assign({}, item)),
        taxOwnerPix: dto.taxOwnerPix,
        taxOwnerTicket: dto.taxOwnerTicket,
        taxOwnerCreditCard: dto.taxOwnerCreditCard,
        taxOwnerPixAutomatic: dto.taxOwnerPixAutomatic,
        receiver: dto.receiver,
        contractualFee: dto.contractualFee || 0,
        taxAUC: dto.taxAUC || 0,
        ticketFixedValue: dto.ticketFixedValue || 0,
        ticketConfigId: dto.ticketConfigId,
        companies: (dto.companies || []).map((company) => JSON.parse(JSON.stringify(company))),
      };
    },
    afterLoad(dto) {
      this.canEditTicketConfig = !!dto.canEditTicketConfig;
      this.accountDebitAvailable = !!dto.accountDebitAvailable;
      this.campaignTypeId = dto.campaignTypeId;
      this.donationForTheNextYear = !!dto.donationForTheNextYear;
    },
    providersFor(methodId) {
      const links = this.lookups.paymentMethodProviderLinks.filter((link) => link.paymentMethodId === methodId);
      const filtered = this.lookups.paymentMethodProviders.filter((provider) => links.some((link) => link.paymentMethodProviderId === provider.id));
      return filtered.length ? filtered : this.lookups.paymentMethodProviders;
    },
    methodName(methodId) {
      const method = this.lookups.paymentMethods.find((item) => item.id === methodId);
      return method ? method.name : "";
    },
    toggleMethod(methodId, checked) {
      const others = this.form.paymentMethodIds.filter((id) => id !== methodId);
      this.form.paymentMethodIds = checked ? others.concat([methodId]) : others;
    },
    validateForm() {
      if (!this.form.providers.creditCard || !this.form.providers.ticket) {
        this.$store.commit("SET_SNACKBAR_MESSAGE", "Escolha os provedores de cartão e de boleto");
        return false;
      }
      return true;
    },
    buildPayload() {
      return Object.assign({}, this.form);
    },
  },
};
</script>
